// useTimeEachMinute.js
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatUTCDate, formatUTCTime } from '../utils/formats';
import { useUser } from 'store/userSlice';
import {
  useClockConnectQuery,
  useFinalizePunchMutation,
  useOfflineMobilePunchMutation,
  usePunchRequestMutation,
  useRefetchClockConnectMutation,
} from 'src/services/webclock';
import { hasValue, useTemp } from 'swipeclock-frontend';
import { attachGeoResult, getGeoLocation } from '../utils/geoLocation';
import { useIsMobileMode, useIsOfflineShellMode } from './useMobileMode';
import { useOfflinePunchQueue } from './useOfflinePunchQueue';
import {
  buildPromptRoutePayload,
  CLOCK_PROMPTS_ROUTE,
  filterAllowedValues,
  getNeededPrompts,
  resolveNeededClockPrompts,
} from '../utils/clockPromptRoutingUtils';
import {
  getOfflineClockConnectCacheRevision,
  isOfflineClockCacheHydrationSettled,
  pickNewerClockConnectPayload,
  readOfflineClockConnectCache,
  recordCompletedMobilePunch,
  subscribeOfflineClockConnectCache,
} from '../utils/offlineClockConnectCache';
import { getWebClockUserStorageKey } from '../utils/webClockUserStorageKey';
import { isAuthError } from '../utils/isNetworkError';
import { useIsApiOffline } from './useApiReachability';

const isBlockingClockConnectError = (error) => {
  if (!error) {
    return false;
  }

  if (isAuthError(error)) {
    return true;
  }

  const message = `${error?.message || ''} ${error?.data?.errorMessage || ''}`
    .toLowerCase()
    .trim();

  return (
    message.includes('unauthorized') ||
    message.includes('not permitted') ||
    message.includes('not enabled') ||
    message.includes('ip=')
  );
};

// ####################
// ### Query Hooks ###
// ####################

export function useClockConnect() {
  const user = useUser();
  const { isMobileMode } = useIsMobileMode();
  const { isOfflineShellMode } = useIsOfflineShellMode();
  const isApiOffline = useIsApiOffline();
  const clockConnect = useClockConnectQuery(
    {
      user,
      mobile: isMobileMode,
    },
    { skip: !hasValue(user?.token) || (isOfflineShellMode && isApiOffline) }
  );

  return clockConnect;
}

export function useClockConnectData() {
  const user = useUser();
  const userKey = getWebClockUserStorageKey(user);
  const { isMobileMode } = useIsMobileMode();
  const { isOfflineShellMode } = useIsOfflineShellMode();
  const { isOffline } = useOfflinePunchQueue();
  const isApiOffline = useIsApiOffline();
  const clockConnect = useClockConnectQuery(
    {
      user,
      mobile: isMobileMode,
    },
    { skip: !hasValue(user?.token) || (isOfflineShellMode && isApiOffline) }
  );
  const [cacheRevision, setCacheRevision] = useState(
    getOfflineClockConnectCacheRevision()
  );

  useEffect(() => {
    const unsubscribe = subscribeOfflineClockConnectCache((nextRevision) => {
      setCacheRevision(nextRevision);
    });

    return unsubscribe;
  }, []);

  const cachedData = useMemo(
    () => readOfflineClockConnectCache(userKey),
    [userKey, cacheRevision]
  );

  const isClockConnectErrorBlocking = isBlockingClockConnectError(
    clockConnect.error
  );
  const canUseCache =
    hasValue(cachedData) &&
    ((isOfflineShellMode && isOffline) ||
      (clockConnect.isError && !isClockConnectErrorBlocking));

  const mergedMobileData = useMemo(() => {
    if (
      !isOfflineShellMode ||
      !hasValue(cachedData) ||
      !hasValue(clockConnect.data)
    ) {
      return null;
    }

    return pickNewerClockConnectPayload(cachedData, clockConnect.data);
  }, [isOfflineShellMode, cachedData, clockConnect.data]);

  const data =
    isOfflineShellMode && isOffline && hasValue(cachedData)
      ? cachedData
      : mergedMobileData ??
        clockConnect.data ??
        (canUseCache ? cachedData : null);
  const isFromCache =
    hasValue(data) &&
    (!clockConnect.data ||
      (isOfflineShellMode && isOffline) ||
      (isOfflineShellMode &&
        hasValue(cachedData) &&
        mergedMobileData === cachedData));
  const awaitingMobileCache =
    isOfflineShellMode &&
    hasValue(userKey) &&
    !isOfflineClockCacheHydrationSettled(userKey);
  const isLoading = !data && (clockConnect.isLoading || awaitingMobileCache);
  const isError = clockConnect.isError && !data && !awaitingMobileCache;

  return {
    ...clockConnect,
    data,
    isLoading,
    isError,
    isFromCache,
    cachedData,
    isClockConnectErrorBlocking,
  };
}

// #####################
// ### Punch  Hooks ###
// #####################

export function usePostPunchRequest() {
  const user = useUser();
  const { data } = useClockConnectData();
  const { responseType, intelligentClock } = data || {};
  const isIntelligent = responseType === 'int' || intelligentClock;
  const isNonIntelligentAndRequiresPunchRequest =
    responseType === 'nint' && !intelligentClock;

  const { isMobileMode } = useIsMobileMode();
  const [postPunchRequest] = usePunchRequestMutation();

  const handlePostPunchRequest = useCallback(
    async (body) => {
      const result = await postPunchRequest({
        ...body,
        mobile: isMobileMode,
        user,
      }).unwrap();
      return result;
    },
    [isMobileMode, postPunchRequest, user]
  );

  if (isIntelligent) {
    return { handlePostPunchRequest, requestType: 'intelligent' };
  }

  if (isNonIntelligentAndRequiresPunchRequest) {
    return {
      handlePostPunchRequest,
      requestType: 'nonIntelligentAndRequiresPunchRequest',
    };
  }

  return { handlePostPunchRequest: () => {}, requestType: 'nonIntelligent' };
}

export function useRouteHandler(requestType) {
  const { data } = useClockConnectData();
  const { temp: cache, setTemp } = useTemp('webClock');

  const [refetchClockConnect] = useRefetchClockConnectMutation();

  const handleRoute = useCallback(
    async (punch, punchResult) => {
      const { clockPrompts: prompts, employeeName } = data || {};

      const clockPrompts = resolveNeededClockPrompts(prompts, punch);

      const params = {
        clockPrompts,
        punch,
        punchResult,
        employeeName,
        refetchClockConnect,
      };

      // Based on requestType, call the appropriate handler
      const handlers = {
        intelligent: handleIntelligentClockCallback,
        nonIntelligentAndRequiresPunchRequest:
          handleNonIntelligentClockCallback,
      };

      const handler =
        handlers[requestType] || handleNonIntelligentClockCallback;

      const { error, data: clockData, route } = await handler(params);

      if (error) {
        // Create rich error object with screenName context
        const errorWithContext = new Error(error.message || error);
        errorWithContext.screenName =
          error.screenName || punchResult?.screenName;
        errorWithContext.reason = error.reason || punchResult?.reason;
        errorWithContext.employeeName = employeeName;
        throw errorWithContext;
      }

      return {
        data: clockData,
        route,
      };
    },
    [data, requestType]
  );

  const setRouteData = useCallback(
    (callbackProps) => {
      const { punchResult } = callbackProps;
      let screenName = punchResult?.screenName;

      // For non-intelligent clocks, set default screenNames
      if (requestType === 'nonIntelligentAndRequiresPunchRequest') {
        const { punch } = callbackProps;
        if (punch === 'PunchIn' && !screenName) {
          screenName = 'ONTIMEIN_SUCCESS';
        }
        if (punch === 'PunchOut' && !screenName) {
          screenName = 'ONTIMEOUT_SUCCESS';
        }
      }

      setTemp({
        screenName,
        employeeName: data?.employeeName ?? cache?.employeeName,
        ...callbackProps,
      });
    },
    [requestType, data?.employeeName, setTemp, cache?.employeeName]
  );

  return { handleRoute, setRouteData };
}

export function useFinalizePunchResult(_requestType) {
  const user = useUser();
  const { isMobileMode } = useIsMobileMode();
  const { isOfflineShellMode } = useIsOfflineShellMode();
  const { data: clockConnectData } = useClockConnectData();

  const [postFinalizePunch] = useFinalizePunchMutation();

  // Builds the finalize-punch body, calls the mutation, and returns the result.
  // By setting all required params and based on requestType, calls the appropriate handler
  // to get back the payload and route. One side-effect sets the route temp data so
  // we have data available after routing if needed.
  const handleFinalizePunchResult = useCallback(
    // punch: 'PunchIn', 'PunchOut', etc.
    // data: { clockPrompts, dataCollectionMeta, expectedPunches, punch }
    // options.signal: an aborted signal skips the finalize request
    async (punch, data, options) => {
      const geoResult = await getGeoLocation(
        isMobileMode,
        clockConnectData?.geoLocationRequired
      );
      if (options?.signal?.aborted) {
        return undefined;
      }
      const punchBody = { ...(data || {}) };
      delete punchBody.geoLocation;
      delete punchBody.geoError;

      const body = {
        ...punchBody,
        ...geoResult,
        dataCollectionMeta: [], // required to avoid 500 error
      };

      try {
        const result = await postFinalizePunch({
          body,
          mobile: isMobileMode,
          punch,
          user,
        }).unwrap();

        if (isOfflineShellMode) {
          const userKey = getWebClockUserStorageKey(user);

          if (hasValue(userKey) && hasValue(punch)) {
            try {
              await Promise.resolve(
                recordCompletedMobilePunch(userKey, {
                  punch,
                  punchDateTime: new Date().toISOString(),
                })
              );
            } catch (recordError) {
              console.warn(
                '[useFinalizePunchResult] Failed to record punch button state.',
                recordError
              );
            }
          }
        }

        return result;
      } catch (error) {
        console.error('Failed to finalize punch:', error);
        attachGeoResult(error, geoResult);
        // Re-throw so the caller can handle routing to an error screen
        throw error;
      }
    },
    [
      postFinalizePunch,
      isMobileMode,
      isOfflineShellMode,
      user,
      clockConnectData?.geoLocationRequired,
    ]
  );

  return { handleFinalizePunchResult };
}

export function useSubmitOfflinePunch() {
  const user = useUser();
  const [offlineMobilePunch] = useOfflineMobilePunchMutation();

  const submitOfflinePunch = useCallback(
    async (queueItem) => {
      const body = {
        clockPrompts: queueItem.clockPrompts || [],
        punchDateTime: queueItem.punchDateTime,
        expectedPunches: [queueItem.punch],
        dataCollectionMeta: [],
      };

      if (hasValue(queueItem.id)) {
        body.punchSessionId = queueItem.id;
      }

      if (hasValue(queueItem.expectedPunches)) {
        body.expectedPunches = queueItem.expectedPunches;
      }

      if (hasValue(queueItem.geoLocation)) {
        body.geoLocation = queueItem.geoLocation;
      }

      if (hasValue(queueItem.geoError)) {
        body.geoError = queueItem.geoError;
      }

      const shouldOverridePunch =
        queueItem.overridePunch || queueItem.isPunchStatusOverride === '1';

      if (shouldOverridePunch) {
        body.isPunchStatusOverride = '1';
      }

      return offlineMobilePunch({
        body,
        punch: queueItem.punch,
        user,
      }).unwrap();
    },
    [offlineMobilePunch, user]
  );

  return { submitOfflinePunch };
}

// ####################
// ### Other Hooks ###
// ####################

export function useLastPunch() {
  const { data } = useClockConnectData();
  const { lastPunchUTC, lastPunch } = data || {
    lastPunchUTC: null,
    lastPunch: '',
  };
  const hasLastPunch = hasValue(lastPunchUTC);

  return {
    hasLastPunch,
    lastPunchDate: hasLastPunch ? formatUTCDate(lastPunchUTC) : null,
    lastPunchType: lastPunch,
    lastPunchTime: hasLastPunch
      ? formatUTCTime(lastPunchUTC, { clockAmPm: true })
      : null,
  };
}

export function useMissingPunches() {
  const { temp: cache, setTemp: setCache } = useTemp('missingPunches');

  const setMissingPunches = useCallback(
    (missingPunches) => {
      setCache({
        missingPunches,
        showMissingPunchAlert: !!missingPunches,
      });
    },
    [setCache]
  );

  return {
    setMissingPunches,
    missingPunches: cache?.missingPunches || false,
    showMissingPunchAlert: cache?.showMissingPunchAlert || false,
  };
}

export function useCachedDataForIntelligentClocks() {
  const { temp: cache } = useTemp('webClock');
  const {
    expectedPunches = [],
    punch,
    requestType,
    overridePunch,
    isPunchStatusOverride,
    punchSessionId,
  } = cache || {};

  return {
    punch,
    expectedPunches,
    requestType,
    overridePunch: overridePunch || isPunchStatusOverride === '1',
    punchSessionId,
  };
}

export {
  resolveNeededClockPrompts,
  buildPromptRoutePayload,
  CLOCK_PROMPTS_ROUTE,
  isBlockingClockConnectError,
};

// ###################################
// ### Helper methods for finalization
// ###################################
// - handleIntelligentClockCallback
// - handleNonIntelligentClockCallback

async function handleIntelligentClockCallback({
  clockPrompts,
  punch,
  punchResult,
  employeeName,
  refetchClockConnect,
}) {
  const { expectedPunches, result, screenName } = punchResult ?? {};

  // ### Error Handling ###
  if (!result) {
    // Note: this can occur if a user clocks in on a clock and then attempts to clock in on another clock
    refetchClockConnect(); // bust the cache to ensure the next punch is successful
    return {
      error: {
        message: punchResult?.reason || 'Punch request failed',
        route: '/clock/web/login',
      },
    };
  }

  // ### Business Logic Errors - Let getErrorRouting handle these ###
  if (
    screenName === 'LOCKOUT_UNSCHEDULED' ||
    screenName === 'LOCKOUT_EARLY' ||
    screenName === 'INMEAL_EARLY'
  ) {
    return {
      error: {
        message: punchResult?.reason || 'Punch not allowed at this time',
        screenName,
        reason: punchResult?.reason,
        employeeName,
      },
    };
  }

  // ### Warning Conditions - Let getErrorRouting handle these ###
  if (
    screenName === 'EARLYIN_SUCCESS' ||
    screenName === 'EARLYOUT_SUCCESS' ||
    screenName === 'LATEIN_SUCCESS' ||
    screenName === 'LATEOUT_SUCCESS'
  ) {
    return {
      error: {
        message: punchResult?.reason || 'Timing warning',
        screenName,
        reason: punchResult?.reason,
        employeeName,
        scheduleStart: punchResult?.scheduleStart,
        scheduleEnd: punchResult?.scheduleEnd,
        time: punchResult?.time,
      },
    };
  }

  if (hasValue(punchResult.dataCollectionMeta)) {
    return {
      error: {
        message: 'Additional data collection required',
        screenName: 'DATA_COLLECTION_WARNING',
      },
    };
  }

  if (hasValue(expectedPunches) && expectedPunches[0] !== punch) {
    return {
      error: {
        message: 'Wrong expected punches',
        screenName: 'WRONG_EXPECTED_PUNCHES',
      },
    };
  }

  // ### Success Handling ###
  const data = buildPromptRoutePayload({
    clockPrompts,
    punch,
    punchResult,
  });

  let route = '/clock/web/pop-ups/punch/alert/success';
  if (clockPrompts.length > 0) {
    route = CLOCK_PROMPTS_ROUTE;
  }

  return { data, route };
}

async function handleNonIntelligentClockCallback({
  clockPrompts,
  punch,
  punchResult,
}) {
  // ### Success Handling ###
  // If there are clockPrompts, we need to redirect to the prompts page;
  // otherwise redirect to the success page
  const data = buildPromptRoutePayload({
    clockPrompts,
    punch,
    punchResult,
  });

  let route = '/clock/web/pop-ups/punch/alert/success';

  if (clockPrompts.length > 0) {
    route = CLOCK_PROMPTS_ROUTE;
  }

  return { data, route };
}

// Re-export for tests that may import from useWebclock
export { filterAllowedValues, getNeededPrompts };
